import React from 'react';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import PlayerName from './player-name';
import TeamName from './team-name';
import Icon from '@components/atoms/icon';
import useWindowSize from '@hooks/useWindowSize';
import { formatCell } from '@lib/page-utils/Opta/format';
import { getByPath } from '@lib/page-utils/Opta/path';
import { isBandedGroup } from '@components/Opta/TableHeaders';
import type { StatColumnGroup } from '@lib/page-utils/Opta/columns';

const LockIcon = dynamic(() => import('@components/lock'));

const MOBILE_BREAKPOINT = 980;

/**
 * Body-cell band classes. The resting tint mirrors the main-header
 * background so a column reads as "belonging" to its group. On row hover
 * every cell swaps to a stronger uniform tint so the entire row lights up
 * crisply, regardless of which group each cell sits under.
 */
const cellBandClass = (groupIndex: number): string =>
  isBandedGroup(groupIndex)
    ? 'bg-primary-50/40 group-hover:bg-primary-100/50'
    : 'bg-transparent group-hover:bg-primary-100/50';

type Row = Record<string, unknown> & {
  id: string;
  /**
   * Cross-season Opta id, supplied on player rows only. `id` is re-minted every
   * season, so this is the identifier the player profile route resolves.
   */
  optaUuid?: string;
  displayName?: string;
  name?: string;
  shortName?: string;
  position?: 'GK' | 'DEF' | 'MID' | 'FWD';
  price?: number;
  team?: { shortName?: string };
};

type TableBodyProps = {
  format: 'players' | 'teams';
  rows: Row[];
  groups: StatColumnGroup[];
  aggregation: 'total' | 'perAppearance' | 'perStart' | 'per90';
  perMillion: boolean;
  restrictContent: boolean;
  openPaywall: () => void;
  /** Player ids ticked into the selected-players filter (players only). */
  selectedPlayerIds?: string[];
  /** Renders a selection tick beside each player name when provided. */
  onTogglePlayerSelection?: (playerId: string) => void;
};

/**
 * The stats API returns participation as raw totals in every aggregation
 * mode (only the stat columns are aggregated server-side), so the MINS
 * column derives its value here: average minutes per appearance or per
 * start, and total minutes otherwise. Per 90 keeps the total — minutes
 * per 90 minutes is meaningless, and the total is useful context for
 * judging a per-90 rate's sample size.
 */
const deriveDisplayedMinutes = (
  participation: { appearances?: number; starts?: number; minutes?: number },
  aggregation: TableBodyProps['aggregation']
): number | undefined => {
  const { appearances, starts, minutes } = participation;
  if (minutes === undefined) return undefined;

  if (aggregation === 'perAppearance') {
    return appearances ? Math.round(minutes / appearances) : undefined;
  }
  if (aggregation === 'perStart') {
    return starts ? Math.round(minutes / starts) : undefined;
  }
  return minutes;
};

const positionTone: Record<NonNullable<Row['position']>, string> = {
  GK: 'bg-amber-50 text-amber-700 ring-amber-200',
  DEF: 'bg-blue-50 text-blue-700 ring-blue-200',
  MID: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  FWD: 'bg-rose-50 text-rose-700 ring-rose-200'
};

const PositionBadge = ({ position }: { position?: Row['position'] }) => {
  if (!position) return null;
  return (
    <span
      className={classNames(
        'inline-flex items-center justify-center rounded-full px-1.5 py-0.5',
        'text-[9px] font-semibold uppercase tracking-wider ring-1 ring-inset',
        positionTone[position]
      )}
    >
      {position}
    </span>
  );
};

const TableBody = ({
  format,
  rows,
  groups,
  aggregation,
  perMillion,
  restrictContent,
  openPaywall,
  selectedPlayerIds,
  onTogglePlayerSelection
}: TableBodyProps) => {
  const { width } = useWindowSize();
  const isDesktop = width > MOBILE_BREAKPOINT;

  const visibleGroups = groups
    .filter((group) => isDesktop || !group.hideOnMobile)
    .map((group) => ({
      ...group,
      columns: group.columns.filter(
        (column) => isDesktop || !column.hideOnMobile
      )
    }))
    .filter((group) => group.columns.length > 0);

  const stickyCellClass =
    'sticky left-0 z-10 bg-white group-hover:bg-primary-100/50 transition-colors';
  const leadingMetaCellClass =
    'px-3 py-3 text-center text-[11px] tabular-nums text-neutral-600 whitespace-nowrap';

  return (
    <tbody>
      {rows.map((row) => {
        const price = typeof row.price === 'number' ? row.price : null;
        const participation =
          (row.participation as
            | {
                appearances?: number;
                starts?: number;
                minutes?: number;
                gamesPlayed?: number;
              }
            | undefined) ?? {};
        const displayedMinutes = deriveDisplayedMinutes(
          participation,
          aggregation
        );

        return (
          <tr
            key={row.id}
            data-cy={`opta-row-${format === 'players' ? 'player' : 'team'}`}
            className="group border-b border-neutral-100 last:border-b-0 transition-colors"
          >
            {format === 'players' ? (
              <>
                <td
                  className={classNames(
                    stickyCellClass,
                    'py-3 pl-4 pr-3 text-left text-xs whitespace-nowrap'
                  )}
                >
                  <div className="flex items-center gap-2">
                    {onTogglePlayerSelection && (
                      <button
                        type="button"
                        data-cy="opta-player-select-toggle"
                        aria-pressed={selectedPlayerIds?.includes(row.id)}
                        aria-label={`${
                          selectedPlayerIds?.includes(row.id) ? 'Remove' : 'Add'
                        } ${row.displayName ?? 'player'} ${
                          selectedPlayerIds?.includes(row.id) ? 'from' : 'to'
                        } the selected players filter`}
                        onClick={() => onTogglePlayerSelection(row.id)}
                        className={classNames(
                          'shrink-0 transition-colors',
                          selectedPlayerIds?.includes(row.id)
                            ? 'text-primary-600 hover:text-primary-700'
                            : 'text-neutral-300 hover:text-primary-400'
                        )}
                      >
                        <Icon name="CheckCircle" className="h-4 w-4" />
                      </button>
                    )}
                    <PositionBadge position={row.position} />
                    <PlayerName item={row} />
                  </div>
                </td>
                {isDesktop && (
                  <>
                    <td
                      className={classNames(
                        leadingMetaCellClass,
                        'font-medium text-neutral-700 group-hover:bg-primary-100/50 transition-colors'
                      )}
                    >
                      {price !== null ? (
                        <span className="inline-flex items-baseline gap-0.5">
                          <span className="text-neutral-400 text-[9px]">£</span>
                          <span>{price.toFixed(1)}</span>
                          <span className="text-neutral-400 text-[9px]">m</span>
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td
                      className={classNames(
                        leadingMetaCellClass,
                        'group-hover:bg-primary-100/50 transition-colors'
                      )}
                    >
                      {participation.appearances ?? '-'}
                    </td>
                    <td
                      className={classNames(
                        leadingMetaCellClass,
                        'group-hover:bg-primary-100/50 transition-colors'
                      )}
                    >
                      {displayedMinutes ?? '-'}
                    </td>
                  </>
                )}
              </>
            ) : (
              <>
                <td
                  className={classNames(
                    stickyCellClass,
                    'py-3 pl-4 pr-3 text-left text-xs whitespace-nowrap'
                  )}
                >
                  <TeamName href={`/clubs/${row.id}`} item={row} />
                </td>
                {isDesktop && (
                  <td
                    className={classNames(
                      leadingMetaCellClass,
                      'group-hover:bg-primary-100/50 transition-colors'
                    )}
                  >
                    {participation.gamesPlayed ?? '-'}
                  </td>
                )}
              </>
            )}

            {visibleGroups.map((group, groupIndex) => (
              <React.Fragment key={`${group.label}-${groupIndex}`}>
                {group.columns.map((column, columnIndex) => {
                  const value = getByPath(row, column.path);
                  return (
                    <td
                      key={`${column.path}-${columnIndex}`}
                      className={classNames(
                        'px-3 py-3 text-center text-xs tabular-nums text-neutral-800 whitespace-nowrap',
                        'transition-colors',
                        cellBandClass(groupIndex),
                        columnIndex === 0 && 'border-l border-neutral-200/70'
                      )}
                    >
                      {restrictContent ? (
                        <LockIcon doAction={openPaywall} />
                      ) : (
                        formatCell(
                          value,
                          column.format,
                          aggregation,
                          perMillion,
                          price
                        )
                      )}
                    </td>
                  );
                })}
              </React.Fragment>
            ))}
          </tr>
        );
      })}
    </tbody>
  );
};

export default TableBody;
